/**
 * Enum → i18n label helpers for the obradors feature.
 *
 * Every enum tuple comes from @obrava/shared (single source of truth); the
 * visible labels are resolved from the platformConsole.* namespaces. Keeping the
 * option builders here means the list filters, the create form, and the detail
 * editor all render the exact same option sets and labels.
 */

import type { TFunction } from 'i18next';
import {
  TENANT_STATUSES,
  TENANT_SECTORS,
  TENANT_PLANS,
  APP_LANGUAGES,
  AUDIT_ACTIONS,
  AUDITED_ENTITY_TYPES,
  type TenantStatus,
  type TenantSector,
  type TenantPlan,
  type AppLanguage,
} from '@obrava/shared';
import type { SelectOption } from '../../components/Select.js';

export function statusLabel(t: TFunction, status: TenantStatus): string {
  return t(`platformConsole.statusLabel:${status}`);
}

export function sectorLabel(t: TFunction, sector: TenantSector): string {
  return t(`platformConsole.sectorLabel:${sector}`);
}

export function planLabel(t: TFunction, plan: TenantPlan): string {
  return t(`platformConsole.planLabel:${plan}`);
}

export function languageLabel(t: TFunction, language: AppLanguage): string {
  return t(`platformConsole.common:lang_${language}`);
}

export function statusOptions(t: TFunction): readonly SelectOption[] {
  return TENANT_STATUSES.map((s) => ({ value: s, label: statusLabel(t, s) }));
}

export function sectorOptions(t: TFunction): readonly SelectOption[] {
  return TENANT_SECTORS.map((s) => ({ value: s, label: sectorLabel(t, s) }));
}

export function planOptions(t: TFunction): readonly SelectOption[] {
  return TENANT_PLANS.map((p) => ({ value: p, label: planLabel(t, p) }));
}

export function languageOptions(t: TFunction): readonly SelectOption[] {
  return APP_LANGUAGES.map((l) => ({ value: l, label: languageLabel(t, l) }));
}

// ── Activity log (F0.9) — action + entity labels/options ──────────────────────
// The labels live in the shared domain.audit namespace so the console and the
// backoffice viewer read identical strings. Unknown values fall back to the raw
// value (defaultValue), so a future module's entries still render.

export function auditActionLabel(t: TFunction, action: string): string {
  return t(`domain.audit:action_${action}`, { defaultValue: action });
}

export function auditEntityLabel(t: TFunction, entity: string): string {
  return t(`domain.audit:entity_${entity}`, { defaultValue: entity });
}

/** Action filter options ('all' sentinel first, mirroring the list filters). */
export function auditActionOptions(t: TFunction): readonly SelectOption[] {
  return [
    { value: 'all', label: t('platformConsole.detail:activityFilterAllActions') },
    ...AUDIT_ACTIONS.map((a) => ({ value: a, label: auditActionLabel(t, a) })),
  ];
}

/** Entity filter options ('all' sentinel first). */
export function auditEntityOptions(t: TFunction): readonly SelectOption[] {
  return [
    { value: 'all', label: t('platformConsole.detail:activityFilterAllEntities') },
    ...AUDITED_ENTITY_TYPES.map((e) => ({ value: e, label: auditEntityLabel(t, e) })),
  ];
}
